// Admin tab for managing per-model reference documents (wiring diagrams, P&ID,
// brochures, IOM, service manuals, certifications). Each row is a (model,
// category) cell with an upload slot; uploading replaces any previous file
// for the same pair (backend enforces UNIQUE(model, category)).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Upload, FileText, Trash2, Eye, Loader2, Library, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Must mirror ALLOWED_CATEGORIES on the backend AND STATIC_CATEGORIES +
// drawing columns in SubmittalPackageTab.jsx so the admin grid stays in sync
// with the engineer-facing file list.
const CATEGORIES = [
  { key: "wiring", label: "Wiring Diagrams" },
  { key: "pid", label: "P&ID Diagrams" },
  { key: "brochure", label: "Product Brochures" },
  { key: "iom", label: "IOM Manuals" },
  { key: "service", label: "Service Manuals" },
  { key: "cert", label: "Certifications" },
  { key: "drawing_ip", label: "Submittal Drawing (IP)" },
  { key: "drawing_si", label: "Submittal Drawing (SI)" },
];

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function AdminModelAssetsTab() {
  const [assets, setAssets] = useState([]); // [{id, model, category, ...}]
  const [models, setModels] = useState([]); // [{model}]
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // `${model}__${category}` for upload/delete in-flight
  const fileInputRefs = useRef({}); // hidden <input> refs keyed by `${model}__${category}`

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Use /known-models which UNIONs both coils + airflow_data tables so
      // every model — including ones that only have airflow data and no
      // coil rows (Model L-L, L-T, etc.) — appears in the matrix.
      const [modelsResp, assetsResp] = await Promise.all([
        axios.get(`${API}/known-models`),
        axios.get(`${API}/model-assets`),
      ]);
      // Internal / non-marketed models that should NOT appear in the admin
      // uploader matrix. PAC is the central AHU component, Op the option
      // module, Spinnaker / ACB are auxiliary parts — engineers never
      // submit them as standalone units to customers.
      const HIDDEN_MODELS = new Set(["PAC", "Model Op", "Spinnaker", "ACB"]);
      const rawModels = Array.isArray(modelsResp.data?.models)
        ? modelsResp.data.models
            .map((m) => (typeof m === "string" ? m : m?.model))
            .filter((m) => m && !HIDDEN_MODELS.has(m))
        : [];
      setModels(rawModels);
      setAssets(assetsResp.data || []);
    } catch (err) {
      toast.error("Failed to load assets: " + formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Group ALL assets for (model, category) — multi-file support. Each cell
  // can now hold multiple labelled variants (e.g. drawing IP 2-pipe + 4-pipe).
  const byKey = useMemo(() => {
    const m = new Map();
    assets.forEach((a) => {
      const k = `${a.model}__${a.category}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    });
    // Sort each cell's files by uploaded_at desc so the newest variant is on top.
    for (const arr of m.values()) {
      arr.sort((x, y) => (y.uploaded_at || "").localeCompare(x.uploaded_at || ""));
    }
    return m;
  }, [assets]);

  const triggerUpload = (model, category) => {
    const key = `${model}__${category}`;
    fileInputRefs.current[key]?.click();
  };

  const handleFileChange = async (e, model, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      toast.error("PDF files only");
      e.target.value = "";
      return;
    }
    // Optional label so engineers can distinguish 2-pipe vs 4-pipe etc.
    // Cancel → null; we still upload with an empty label (treated as "default").
    // eslint-disable-next-line no-restricted-globals
    const label = window.prompt(
      `Label for "${file.name}" (optional — e.g. "2-pipe", "4-pipe", "Rev B"):`,
      ""
    );
    if (label === null) {
      // User cancelled the prompt — abort the upload.
      e.target.value = "";
      return;
    }
    const key = `${model}__${category}`;
    setBusyKey(key);
    try {
      const form = new FormData();
      form.append("model", model);
      form.append("category", category);
      form.append("label", label.trim());
      form.append("file", file);
      await axios.post(`${API}/admin/model-assets/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${file.name}`);
      await refresh();
    } catch (err) {
      toast.error("Upload failed: " + formatApiError(err));
    } finally {
      setBusyKey(null);
      e.target.value = ""; // reset so the same file can be re-selected
    }
  };

  const handleDelete = async (asset) => {
    if (!asset) return;
    // Single browser confirm is enough — assets are reference docs, not data.
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${asset.filename}"?`)) return;
    const key = `${asset.model}__${asset.category}`;
    setBusyKey(key);
    try {
      await axios.delete(`${API}/admin/model-assets/${asset.id}`);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error("Delete failed: " + formatApiError(err));
    } finally {
      setBusyKey(null);
    }
  };

  const handlePreview = (asset) => {
    if (!asset) return;
    // The download endpoint streams the PDF; opening it in a new tab gives
    // an in-browser preview without us bundling a viewer.
    const url = `${API}/model-assets/${asset.id}/download`;
    // Need to include the Authorization header — we can't put it on a
    // window.open call, so fetch as blob and open the blob URL instead.
    axios
      .get(url, { responseType: "blob" })
      .then((resp) => {
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch((err) => {
        toast.error("Preview failed: " + formatApiError(err));
      });
  };

  // Toolbar count: total uploaded files and how many cells have at least one.
  const filledCells = byKey.size;
  const totalCells = models.length * CATEGORIES.length;

  return (
    <Card data-testid="admin-tab-documents-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Library className="h-4 w-4" /> Model Documents
          {totalCells > 0 && (
            <span className="text-xs font-normal text-muted-foreground ml-2">
              {filledCells} / {totalCells} uploaded
            </span>
          )}
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
          data-testid="admin-assets-refresh"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-1" />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Each cell may hold multiple labelled variants (e.g. Submittal Drawing
          IP for 2-pipe AND 4-pipe). Click <strong>+ Add</strong> to upload a
          new variant; existing files stay until you delete them. Maximum
          50 MB per file. PDF only.
        </p>
        {loading && assets.length === 0 ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
          </div>
        ) : models.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-6 text-center">
            No models in the database yet. Add coil data to populate the matrix.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-sm min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">Model</TableHead>
                  {CATEGORIES.map((c) => (
                    <TableHead key={c.key} className="text-center min-w-[150px]">
                      {c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {models.map((m) => (
                  <TableRow key={m} data-testid={`admin-assets-row-${m.replace(/\s+/g, "_")}`}>
                    <TableCell className="font-semibold text-foreground">{m}</TableCell>
                    {CATEGORIES.map((c) => {
                      const key = `${m}__${c.key}`;
                      const cellAssets = byKey.get(key) || [];
                      const busy = busyKey === key;
                      return (
                        <TableCell key={c.key} className="text-center align-top">
                          <input
                            ref={(el) => { fileInputRefs.current[key] = el; }}
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, m, c.key)}
                            data-testid={`admin-assets-input-${key}`}
                          />
                          {cellAssets.length > 0 ? (
                            <div className="flex flex-col items-stretch gap-1.5">
                              {cellAssets.map((asset) => (
                                <div
                                  key={asset.id}
                                  className="flex flex-col items-center gap-0.5 rounded border border-border/60 bg-muted/30 px-1.5 py-1"
                                  data-testid={`admin-assets-item-${asset.id}`}
                                >
                                  {asset.label && (
                                    <div className="text-[10px] font-semibold text-primary uppercase tracking-wide leading-none mb-0.5">
                                      {asset.label}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1 text-emerald-700">
                                    <FileText className="h-3.5 w-3.5 shrink-0" />
                                    <span className="text-xs font-medium truncate max-w-[140px]" title={asset.filename}>
                                      {asset.filename}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {formatBytes(asset.size_bytes)} · {formatDate(asset.uploaded_at)}
                                  </div>
                                  <div className="flex items-center gap-0.5 mt-0.5">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => handlePreview(asset)}
                                      title="Preview"
                                      data-testid={`admin-assets-preview-${asset.id}`}
                                    >
                                      <Eye className="h-3 w-3 text-muted-foreground" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                      onClick={() => handleDelete(asset)}
                                      disabled={busy}
                                      title="Delete"
                                      data-testid={`admin-assets-delete-${asset.id}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => triggerUpload(m, c.key)}
                                disabled={busy}
                                className="h-7 text-xs"
                                data-testid={`admin-assets-add-${key}`}
                              >
                                {busy ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="h-3 w-3 mr-1" />
                                )}
                                + Add
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => triggerUpload(m, c.key)}
                              disabled={busy}
                              className="text-xs"
                              data-testid={`admin-assets-upload-${key}`}
                            >
                              {busy ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5 mr-1" />
                              )}
                              Upload
                            </Button>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
